import { useState, useEffect } from "react";
import CommonForm from "../../../../components/form/form";
import CommonTable from "../../../../components/table/table";
import DefaultButton from "../../../../components/buttons/defaultButton";
import StaffModal from "../../../../components/modals/staffModal";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import { useFilterOptions } from "../../../../../hooks";
import { getUserList } from "../../../../../services/workspace/user.service";
import toast from "react-hot-toast";
import { convertToLabelValue, _GenderData } from "../../../../../utils/common";
import { useCommonFetch } from "../../../../../hooks/useCommonFetch";
import { getLeaveAllocationHistory, getBulkLeaveAllocationHistory } from "../../../../../services/workspace/leave.service";
import { createDataTransformer } from "../../../../../utils/dataTransformers";
import { useDispatch, useSelector } from "react-redux";
import { fetchStaffList } from "../../../../../store/slices/user.slice";

const LeaveAllocationSection = ({ 
  leaveTypes = [],
  onIndividualAllocation,
  onBulkAllocation,
  onAnnualAllocation
}) => {
  const [allocationMode, setAllocationMode] = useState("individual"); // individual, bulk, annual, or history
  const [historyFilterType, setHistoryFilterType] = useState("all"); // all, individual, bulk, annual
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);

  const dispatch = useDispatch();

const { staffList, staffLoading } = useSelector((state) => state.user);

useEffect(() => {
    dispatch(fetchStaffList({ type: 2}));
  }, [dispatch]);
  
  // History filter state
  const [historyFilters, setHistoryFilters] = useState({
    user_id: "",
    leave_type_id: "",
    department_id: "",
  });
  const [allocationFormData, setAllocationFormData] = useState({
    employee_id: "",
    leave_type_id: "",
    days: "",
    allocation_date: "",
    valid_until: "",
    reason: "",
    gender_filter: "",
  });
  const [bulkAllocationData, setBulkAllocationData] = useState({
    department_id: "", // Single select for department
    designation_id: [], // Array for multiple designations
    leave_type_id: "",
    gender_filter: "",
    days: "",
    allocation_date: "",
    valid_until: "",
  });

  // Annual Allocation state
  const [annualAllocationData, setAnnualAllocationData] = useState({
    year: new Date().getFullYear().toString(),
    allocation_date: "",
    valid_until: "",
    description: "", // Description for allocation
  });

  // Store leave type days allocation: { leave_type_id: days }
  const [leaveTypeDays, setLeaveTypeDays] = useState({});

  // Common data transformer for history
  const historyDataTransformer = createDataTransformer({
    employee: { transformer: (item) => ({ data: item.user?.name || item.employee_name || "-", variant: "text" }) },
    leave_type: { transformer: (item) => ({ data: item.leaveType?.name || item.leaveType?.code || item.leave_type || "-", variant: "text" }) },
    days: { transformer: (item) => ({ data: item.days || item.allotted || "0", variant: "text" }) },
    allocation_date: { transformer: (item) => ({ data: item.allocation_date || item.start_date || item.created_at || "-", variant: "date" }) },
    valid_until: { transformer: (item) => ({ data: item.valid_until || item.end_date || "-", variant: "date" }) },
    allocated_by: { transformer: (item) => ({ data: item.allocated_by || item.created_by_name || item.allocated_by_name || "-", variant: "text" }) },
    reason: { transformer: (item) => ({ data: item.reason || item.description || "-", variant: "text" }) },
    allocation_type: { transformer: (item) => {
      let type = "Individual";
      let badgeColor = "bg-blue-100 text-blue-700";
      
      if (item.allocation_type) {
        if (item.allocation_type === "bulk" || item.allocation_type === "Bulk") {
          type = "Bulk";
          badgeColor = "bg-purple-100 text-purple-700";
        } else if (item.allocation_type === "annual" || item.allocation_type === "Annual") {
          type = "Annual";
          badgeColor = "bg-green-100 text-green-700";
        }
      } else if (item.department_id || item.designation_id) {
        type = "Bulk";
        badgeColor = "bg-purple-100 text-purple-700";
      } else if (item.year || item.allocation_type === "by_template") {
        type = "Annual";
        badgeColor = "bg-green-100 text-green-700";
      }
      
      return { 
        data: type, 
        variant: "badge",
        className: badgeColor
      };
    }},
    status: { transformer: (item) => ({ 
      data: item.status || (item.valid_until && new Date(item.valid_until) < new Date() ? "Expired" : "Active"), 
      variant: "text" 
    }) },
  });

  // Individual Allocation History
  const {
    loading: individualHistoryLoading,
    data: individualHistoryTableData,
    total: individualHistoryTotalData,
    currentPage: individualHistoryCurrentPage,
    search: individualHistorySearch,
    handleSearchChange: handleIndividualHistorySearchChange,
    handlePageChange: handleIndividualHistoryPageChange,
    sortingUIProps: individualHistorySortingUIProps,
    setCustomParams: setIndividualHistoryCustomParams,
    fetchData: fetchIndividualHistoryData,
  } = useCommonFetch(getLeaveAllocationHistory, {
    dataPath: "leaveAllocation.data",
    totalPath: "leaveAllocation.meta.total",
    errorMessage: "Failed to fetch individual leave allocation history!",
    debounceDelay: 500,
    sortingOptions: [
      { label: "Employee", field: "employee_name" },
      { label: "Leave Type", field: "leave_type" },
      { label: "Allocation Date", field: "allocation_date" },
      { label: "Days", field: "days" },
    ],
    sortingModule: "leaveAllocation",
    orderParamNames: { orderByKey: "order_by", orderTypeKey: "order_type" },
    dataTransformer: historyDataTransformer,
  });

  // Bulk Allocation History
  const {
    loading: bulkHistoryLoading,
    data: bulkHistoryTableData,
    total: bulkHistoryTotalData,
    currentPage: bulkHistoryCurrentPage,
    search: bulkHistorySearch,
    handleSearchChange: handleBulkHistorySearchChange,
    handlePageChange: handleBulkHistoryPageChange,
    sortingUIProps: bulkHistorySortingUIProps,
    setCustomParams: setBulkHistoryCustomParams,
    fetchData: fetchBulkHistoryData,
  } = useCommonFetch(getBulkLeaveAllocationHistory, {
    dataPath: "leaveAllocation.data",
    totalPath: "leaveAllocation.meta.total",
    errorMessage: "Failed to fetch bulk leave allocation history!",
    debounceDelay: 500,
    sortingOptions: [
      { label: "Employee", field: "employee_name" },
      { label: "Leave Type", field: "leave_type" },
      { label: "Allocation Date", field: "allocation_date" },
      { label: "Days", field: "days" },
    ],
    sortingModule: "leaveAllocation",
    orderParamNames: { orderByKey: "order_by", orderTypeKey: "order_type" },
    dataTransformer: historyDataTransformer,
  });

  // Determine which history data to use based on filter type
  const historyLoading = historyFilterType === "individual" ? individualHistoryLoading : 
                        historyFilterType === "bulk" ? bulkHistoryLoading : 
                        individualHistoryLoading;
  const historyTableData = historyFilterType === "individual" ? individualHistoryTableData : 
                           historyFilterType === "bulk" ? bulkHistoryTableData : 
                           individualHistoryTableData;
  const historyTotalData = historyFilterType === "individual" ? individualHistoryTotalData : 
                          historyFilterType === "bulk" ? bulkHistoryTotalData : 
                          individualHistoryTotalData;
  const historyCurrentPage = historyFilterType === "individual" ? individualHistoryCurrentPage : 
                            historyFilterType === "bulk" ? bulkHistoryCurrentPage : 
                            individualHistoryCurrentPage;
  const historySearch = historyFilterType === "individual" ? individualHistorySearch : 
                       historyFilterType === "bulk" ? bulkHistorySearch : 
                       individualHistorySearch;
  const handleHistorySearchChange = historyFilterType === "individual" ? handleIndividualHistorySearchChange : 
                                   historyFilterType === "bulk" ? handleBulkHistorySearchChange : 
                                   handleIndividualHistorySearchChange;
  const handleHistoryPageChange = historyFilterType === "individual" ? handleIndividualHistoryPageChange : 
                                 historyFilterType === "bulk" ? handleBulkHistoryPageChange : 
                                 handleIndividualHistoryPageChange;
  const historySortingUIProps = historyFilterType === "individual" ? individualHistorySortingUIProps : 
                               historyFilterType === "bulk" ? bulkHistorySortingUIProps : 
                               individualHistorySortingUIProps;

  // Filter history by allocation type and apply filters
  useEffect(() => {
    if (allocationMode === "history") {
      const params = {};
      
      if (historyFilterType === "individual") {
        // Individual filters: user_id, leave_type_id
        if (historyFilters.user_id) params.user_id = historyFilters.user_id;
        if (historyFilters.leave_type_id) params.leave_type_id = historyFilters.leave_type_id;
        setIndividualHistoryCustomParams(params);
        fetchIndividualHistoryData(params, true);
      } else if (historyFilterType === "bulk") {
        // Bulk filters: department_id, leave_type_id
        if (historyFilters.department_id) params.department_id = historyFilters.department_id;
        if (historyFilters.leave_type_id) params.leave_type_id = historyFilters.leave_type_id;
        setBulkHistoryCustomParams(params);
        fetchBulkHistoryData(params, true);
      } else {
        // "all" - show individual by default
        if (historyFilters.user_id) params.user_id = historyFilters.user_id;
        if (historyFilters.leave_type_id) params.leave_type_id = historyFilters.leave_type_id;
        setIndividualHistoryCustomParams(params);
        fetchIndividualHistoryData(params, true);
      }
    }
  }, [historyFilterType, historyFilters, allocationMode]);

  // Fetch departments and designations using useFilterOptions hook for better caching
  const { 
    departments, 
    departmentsLoading, 
    refresh: refreshDepartments,
    designations,
    designationsLoading,
    fetchDesignationsByDepartment
  } = useFilterOptions(["departments", "designations"]);
  
 
  const [filteredDesignations, setFilteredDesignations] = useState([]);

  // Fetch staff list
  // useEffect(() => {
  //   const fetchStaff = () => {
  //     setStaffLoading(true);
  //     getUserList()
  //       .then((response) => {
  //         setStaffLoading(false);
  //         setStaffList(convertToLabelValue(response.user?.data || []) || []);
  //       })
  //       .catch((error) => {
  //         toast.error("Failed to fetch staff data!");
  //         console.log(error);
  //         setStaffLoading(false);
  //       });
  //   };
  //   fetchStaff();
  // }, []);

  // Initialize filtered designations with all designations
  useEffect(() => {
    setFilteredDesignations(designations || []);
  }, [designations]);

  // Handle department change for bulk allocation
  const handleDepartmentChange = async (departmentId) => {
    if (departmentId) {
      // Fetch designations for selected department
      const designationsForDept = await fetchDesignationsByDepartment(departmentId);
      setFilteredDesignations(designationsForDept || []);
    } else {
      // No department selected, show all designations
      setFilteredDesignations(designations || []);
    }
  };

  // Prepare annual allocation payload for API
  const handleAnnualAllocationSubmit = () => {
    
    // Create leave_types array from leaveTypeDays state
    const leave_types = Object.entries(leaveTypeDays)
      .filter(([id, days]) => days && parseInt(days) > 0) // Only include leave types with days > 0
      .map(([id, days]) => ({
        id: parseInt(id), // Convert string id to number
        allotted: parseInt(days)
      }));

    // Validate: At least one leave type must have days allocated
    if (leave_types.length === 0) {
      toast.error("Please allocate days for at least one leave type!");
      return;
    }

    // Create final payload matching API structure - Always allocate to all employees
    const payload = {
      year: parseInt(annualAllocationData.year),
      applicable_to: "all", // Always allocate to all employees
      description: annualAllocationData.description || `Annual Leave Allocation ${annualAllocationData.year}`,
      start_date: annualAllocationData.allocation_date,
      end_date: annualAllocationData.valid_until,
      leave_types
    };

    
    // Call the parent handler
    if (onAnnualAllocation) {
      onAnnualAllocation(payload);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setAllocationFormData({
      ...allocationFormData,
      employee_id: employee?.value || "",
    });
    setShowStaffModal(false);
    
    // Check if selected leave type is gender-specific
    if (selectedLeaveType && selectedLeaveType.gender_specific && selectedLeaveType.gender_specific !== "all") {
      // Show warning if employee gender doesn't match
      // Note: You'll need to get employee gender from employee object or API
    }
  };

  // Get gender-specific leave types
  const getGenderSpecificLeaves = (gender) => {
    if (!gender || gender === "all") return leaveTypes;
    return leaveTypes.filter(lt => 
      !lt.gender_specific || lt.gender_specific === "all" || lt.gender_specific === gender
    );
  };

  const individualAllocationFields = [
    {
      name: "employee_id",
      label: "Select Staff",
      type: "select",
      placeholder: "Search and select staff member",
      required: true,
      options: staffList,
      colSpan: 12,
      disabled: staffLoading,
      info: "Select the staff member for whom you want to allocate leave. Search by name or employee ID.",
      searchable: true,
    },
    {
      name: "leave_type_id",
      label: "Leave Type",
      type: "select",
      options: leaveTypes,
      placeholder: "Select leave type",
      required: true,
      colSpan: 6,
      info: "Select the type of leave to allocate (CL, SL, EL, etc.). This determines which leave balance will be updated for the employee. Note: Gender-specific leaves (like Maternity) will only be allocated to eligible employees.",
    },
    {
      name: "days",
      label: "Number of Days",
      type: "number",
      placeholder: "Enter number of days",
      required: true,
      colSpan: 6,
      info: "Number of leave days to allocate. Example: 12 days for annual CL allocation. This will be added to employee's current balance.",
    },
    {
      name: "allocation_date",
      label: "Allocation Date",
      type: "date",
      placeholder: "Select allocation date",
      required: true,
      colSpan: 6,
      info: "Date when leave is allocated. Usually today's date or start of financial year. Used for tracking and reporting.",
    },
    {
      name: "valid_until",
      label: "Valid Until",
      type: "date",
      placeholder: "Select expiry date (optional)",
      colSpan: 6,
      info: "Optional expiry date for allocated leave. If set, leave expires after this date. Leave it empty for permanent allocation.",
    },
    {
      name: "reason",
      label: "Allocation Reason",
      type: "textarea",
      placeholder: "Enter reason for allocation (e.g., Annual allocation, Bonus leave, etc.)",
      colSpan: 12,
      info: "Reason for this allocation. Example: 'Annual CL allocation for 2024', 'Bonus leave for performance', 'Correction for previous error'. This is logged in audit trail.",
    },
  ];

  const bulkAllocationFields = [
    {
      name: "department_id",
      label: "Department",
      type: "select",
      options: [], // Will be populated from Redux or API
      placeholder: "Select department (optional)",
      colSpan: 6,
      info: "Select a department to allocate leave to all employees in that department. Leave empty to allocate to all departments.",
    },
    {
      name: "designation_id",
      label: "Designation(s)",
      type: "select",
      multiple: true, // For Yup validation - tells it to expect array
      isMulti: true, // For React-Select component - enables multi-select UI
      options: [], // Will be populated from Redux or API
      placeholder: "Select one or more designations (optional)",
      colSpan: 6,
      info: "Select multiple designations to allocate leave to employees with selected designations. Leave empty to allocate to all designations.",
    },
    {
      name: "leave_type_id",
      label: "Leave Type",
      type: "select",
      options: leaveTypes,
      placeholder: "Select leave type",
      required: true,
      colSpan: 6,
      info: "Select the type of leave to allocate to all selected employees. If leave is gender-specific, only eligible employees will receive it.",
    },
    {
      name: "gender_filter",
      label: "Gender Filter (Optional)",
      type: "select",
      options: [
        { label: "All Genders", value: "" },
        { label: "Male Only", value: "male" },
        { label: "Female Only", value: "female" },
        { label: "Other Only", value: "other" },
      ],
      placeholder: "Filter by gender (optional)",
      colSpan: 6,
      info: "Optional: Filter employees by gender for allocation. If leave type is gender-specific, this filter will be automatically applied. Example: For Maternity Leave, only Female employees will be allocated.",
    },
    {
      name: "days",
      label: "Number of Days",
      type: "number",
      placeholder: "Enter number of days",
      required: true,
      colSpan: 6,
      info: "Number of leave days to allocate to each employee. Same amount will be allocated to all selected employees.",
    },
    {
      name: "allocation_date",
      label: "Allocation Date",
      type: "date",
      placeholder: "Select allocation date",
      required: true,
      colSpan: 6,
      info: "Date when leave is allocated to all employees.",
    },
    {
      name: "valid_until",
      label: "Valid Until",
      type: "date",
      placeholder: "Select expiry date (optional)",
      colSpan: 6,
      info: "Optional expiry date. All allocated leaves expire after this date.",
    },
  ];

  // Annual Allocation fields
  const annualAllocationFields = [
    {
      name: "year",
      label: "Year",
      type: "select",
      options: Array.from({ length: 5 }, (_, i) => {
        const year = new Date().getFullYear() + i;
        return { label: year.toString(), value: year.toString() };
      }),
      placeholder: "Select year",
      required: true,
      colSpan: 6,
      info: "Select the year for which you want to allocate annual leave. Example: 2024, 2025. This determines the validity period of allocated leaves.",
    },
    {
      name: "allocation_date",
      label: "Allocation Date (Start Date)",
      type: "date",
      placeholder: "Select allocation date",
      required: true,
      colSpan: 6,
      info: "Date when annual leave allocation starts. Usually January 1st of the selected year.",
    },
    {
      name: "valid_until",
      label: "Valid Until",
      type: "date",
      placeholder: "Select expiry date (optional)",
      colSpan: 6,
      info: "Optional expiry date. Usually December 31st of the selected year. Leave empty for full year validity.",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "Enter description for this annual allocation (e.g., 'Annual Leave Allocation 2024')",
      colSpan: 12,
      info: "Optional description to identify this allocation. This helps in tracking and auditing.",
    },
  ];

  return (
    <div className="flex flex-col gap-4 p-4 ">
      {/* Section Description */}
      <div className="bg-information-50 border border-information-200 rounded-lg p-4">
        <h4 className="text-theme font-semibold mb-2 flex items-center gap-2">
          <span>👥</span> About Leave Allocation
        </h4>
        <p className="text-sm text-theme mb-2">
          Allocate leave balances to employees individually or in bulk. This is how employees receive their leave balance for the year or specific periods.
        </p>
        <div className="bg-white rounded p-3 mt-2">
          <p className="text-xs font-medium text-theme mb-1">Example Scenarios:</p>
          <ul className="text-xs text-textLight space-y-1 list-disc list-inside">
            <li><strong>Individual:</strong> Allocate 12 CL days to John Doe for 2024. John's CL balance becomes 12.</li>
            <li><strong>Bulk by Department:</strong> Select "Engineering" department - shows only designations in Engineering dept - then select "Senior Developer" and "Tech Lead" to allocate only to these roles in Engineering.</li>
            <li><strong>Bulk by Designation:</strong> Select "Engineering" department first, then choose multiple designations like "Developer", "Senior Developer" to allocate to all these roles within Engineering dept.</li>
            <li><strong>Annual Allocation:</strong> At year start, allocate annual leave to all employees across the organization. Example: Set 12 CL, 10 SL, 15 EL for year 2024 - all employees get these balances on Jan 1st, 2024.</li>
          </ul>
        </div>
      </div>

      {/* Mode Selection */}
      <div className="bg-white rounded-lg p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setAllocationMode("individual")}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              allocationMode === "individual"
                ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white"
                : "bg-light text-theme hover:bg-bghoverlight"
            }`}
          >
            Individual Allocation
          </button>
          <button
            onClick={() => setAllocationMode("bulk")}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              allocationMode === "bulk"
                ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white"
                : "bg-light text-theme hover:bg-bghoverlight"
            }`}
          >
            Bulk Allocation
          </button>
          <button
            onClick={() => setAllocationMode("annual")}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              allocationMode === "annual"
                ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white"
                : "bg-light text-theme hover:bg-bghoverlight"
            }`}
          >
            Annual Allocation
          </button>
          <button
            onClick={() => setAllocationMode("history")}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              allocationMode === "history"
                ? "bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white"
                : "bg-light text-theme hover:bg-bghoverlight"
            }`}
          >
            Allocation History
          </button>
        </div>
      </div>

      {/* Individual Allocation */}
      {allocationMode === "individual" && (
        <div className="bg-white rounded-lg">
          <h3 className="text-lg font-semibold text-theme mb-2">Individual Leave Allocation</h3>
          <p className="text-sm text-textLight mb-4">
            Allocate leave to a specific employee. Select the employee, leave type, and number of days.
          </p>
          <div className="bg-success-50 border border-success-200 rounded p-3 mb-4">
            <p className="text-xs font-medium text-theme mb-1">✅ Impact:</p>
            <p className="text-xs text-textLight">
              When you allocate leave to an employee, it's added to their leave balance immediately. Example: If employee has 5 CL days and you allocate 12 more, their new balance becomes 17 CL days. This allocation is logged in audit trail.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <CommonForm
              fields={individualAllocationFields}
              initialData={allocationFormData}
              onSubmit={onIndividualAllocation}
              submitButtonText="Allocate Leave"
              showCancelButton={false}
              gap="gap-4"
              fieldClassName="bg-light p-3 rounded"
              onValuesChange={(values, setFieldValue) => {
                setAllocationFormData(values);
                // Update selected employee when form value changes
                if (values.employee_id) {
                  const employee = staffList.find(emp => emp.value === values.employee_id);
                  if (employee) {
                    setSelectedEmployee(employee);
                  }
                }
                // Check if selected leave type is gender-specific
                const selectedLeave = leaveTypes.find(lt => lt.value === values.leave_type_id);
                if (selectedLeave) {
                  setSelectedLeaveType(selectedLeave);
                  if (selectedLeave.gender_specific && selectedLeave.gender_specific !== "all") {
                    const genderLabel = selectedLeave.gender_specific === "female" ? "Female" : 
                                      selectedLeave.gender_specific === "male" ? "Male" : "Other";
                    // Show warning
                  }
                }
              }}
            />
            {selectedLeaveType && selectedLeaveType.gender_specific && selectedLeaveType.gender_specific !== "all" && (
              <div className="bg-warning-50 border border-warning-200 rounded p-3">
                <p className="text-xs font-medium text-theme mb-1">⚠️ Gender-Specific Leave:</p>
                <p className="text-xs text-textLight">
                  <strong>{selectedLeaveType.label}</strong> is restricted to <strong>{selectedLeaveType.gender_specific === "female" ? "Female" : selectedLeaveType.gender_specific === "male" ? "Male" : "Other"}</strong> employees only. 
                  Please ensure the selected employee's gender matches this requirement.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Allocation */}
      {allocationMode === "bulk" && (
        <div className="bg-white rounded-lg ">
          <h3 className="text-lg font-semibold text-theme mb-2">Bulk Leave Allocation</h3>
          <p className="text-sm text-textLight mb-4">
            Allocate leave to multiple employees at once. Select a department and/or multiple designations, or leave both empty to allocate to all employees.
          </p>
          <div className="bg-warning-50 border border-warning-200 rounded p-3 mb-4">
            <p className="text-xs font-medium text-theme mb-1">⚠️ Important:</p>
            <p className="text-xs text-textLight">
              Bulk allocation affects multiple employees. Example: If you select "Engineering" department and "Manager" & "Team Lead" designations, then allocate 10 SL days - only those employees in Engineering department who are Managers or Team Leads will get 10 SL days. This action cannot be easily undone, so verify your selections carefully.
            </p>
            <p className="text-xs text-textLight mt-2">
              <strong>Note:</strong> You can combine filters - selecting both department and designation(s) will allocate only to employees matching BOTH criteria. If you select a gender-specific leave type (like Maternity Leave), only eligible employees will receive the allocation.
            </p>
          </div>
          <CommonForm
            fields={bulkAllocationFields.map(field => {
              if (field.name === "department_id") {
                // Add onChange handler to fetch designations when department changes
                return { 
                  ...field, 
                  options: departments || [], 
                  disabled: departmentsLoading,
                  onChange: (value) => {
                    handleDepartmentChange(value?.value);
                  }
                };
              }
              if (field.name === "designation_id") {
                // Use filtered designations and disable if no department selected
                return { 
                  ...field, 
                  options: filteredDesignations || [], 
                  disabled: !bulkAllocationData.department_id || designationsLoading,
                  info: bulkAllocationData.department_id 
                    ? "Select multiple designations from the selected department." 
                    : "Please select a department first to view available designations."
                };
              }
              return field;
            })}
            initialData={bulkAllocationData}
            onSubmit={onBulkAllocation}
            submitButtonText="Allocate to Selected Employees"
            showCancelButton={false}
            gap="gap-4"
            fieldClassName="bg-light p-3 rounded"
            onValuesChange={(values, setFieldValue) => {
              setBulkAllocationData(values);
              
              // Handle department change
              if (values.department_id !== bulkAllocationData.department_id) {
                // Clear designation selection when department changes
                setFieldValue("designation_id", []);
              }
              
              // Auto-set gender filter if leave type is gender-specific
              const selectedLeave = leaveTypes.find(lt => lt.value === values.leave_type_id);
              if (selectedLeave && selectedLeave.gender_specific && selectedLeave.gender_specific !== "all") {
                setFieldValue("gender_filter", selectedLeave.gender_specific);
              }
            }}
          />
          {bulkAllocationData.leave_type_id && (() => {
            const selectedLeave = leaveTypes.find(lt => lt.value === bulkAllocationData.leave_type_id);
            if (selectedLeave && selectedLeave.gender_specific && selectedLeave.gender_specific !== "all") {
              const genderLabel = selectedLeave.gender_specific === "female" ? "Female" : 
                                selectedLeave.gender_specific === "male" ? "Male" : "Other";
              return (
                <div className="bg-information-50 border border-information-200 rounded p-3 mt-4">
                  <p className="text-xs font-medium text-theme mb-1">ℹ️ Gender-Specific Leave:</p>
                  <p className="text-xs text-textLight">
                    <strong>{selectedLeave.label}</strong> is restricted to <strong>{genderLabel}</strong> employees only. 
                    Only {genderLabel.toLowerCase()} employees in the selected group will receive this allocation.
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Annual Allocation */}
      {allocationMode === "annual" && (
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-lg">
            <h3 className="text-lg font-semibold text-theme mb-2">Annual Leave Allocation</h3>
            <p className="text-sm text-textLight mb-4">
              Allocate annual leave balances to all employees at once. Perfect for year-start allocation across the entire organization.
            </p>
            <div className="bg-success-50 border border-success-200 rounded p-3 mb-4">
              <p className="text-xs font-medium text-theme mb-1">✅ Impact:</p>
              <p className="text-xs text-textLight">
                Annual allocation automatically distributes leave balances to all employees based on your configured rules. Example: For year 2024, all employees get their annual leave balance (e.g., 12 CL, 10 SL, 15 EL) allocated on January 1st, 2024.
              </p>
            </div>
            <CommonForm
              fields={annualAllocationFields}
              initialData={annualAllocationData}
              onSubmit={() => {}} // No submit from this form
              showSubmitButton={false} // Hide the form's submit button
              showCancelButton={false}
              gap="gap-4"
              fieldClassName="bg-light p-3 rounded"
              onValuesChange={(values, setFieldValue) => {
                setAnnualAllocationData(values);
                // Auto-set dates based on year
                if (values.year) {
                  const year = parseInt(values.year);
                  if (!values.allocation_date) {
                    setFieldValue("allocation_date", `${year}-01-01`);
                  }
                  if (!values.valid_until) {
                    setFieldValue("valid_until", `${year}-12-31`);
                  }
                }
              }}
            />
          </div>

          {/* Leave Type Rules Configuration */}
          <div className="bg-white rounded-lg ">
            <h3 className="text-lg font-semibold text-theme mb-2">Configure Leave Allocation Rules</h3>
            <p className="text-sm text-textLight mb-4">
              Set how many days of each leave type should be allocated to all employees for the selected year.
            </p>
            <div className="bg-warning-50 border border-warning-200 rounded p-3 mb-4">
              <p className="text-xs font-medium text-theme mb-1">ℹ️ Gender-Specific Leaves:</p>
              <p className="text-xs text-textLight">
                Gender-specific leaves (like Maternity Leave) will automatically be allocated only to eligible employees based on their gender. The system will filter employees automatically.
              </p>
            </div>
            <div className="bg-light rounded p-4">
              <div className="flex flex-col gap-4">
                {leaveTypes.map((leaveType) => {
                  const isGenderSpecific = leaveType.gender_specific && leaveType.gender_specific !== "all";
                  const genderLabel = isGenderSpecific 
                    ? (leaveType.gender_specific === "female" ? "Female Only" : 
                       leaveType.gender_specific === "male" ? "Male Only" : "Other Only")
                    : "";
                  const leaveTypeId = leaveType.id || leaveType.value; // Use ID field
                  return (
                    <div key={leaveTypeId} className="flex items-center gap-4 bg-white p-3 rounded">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-theme">
                            {leaveType.label} {leaveType.code && `(${leaveType.code})`}
                          </label>
                          {isGenderSpecific && (
                            <span className="text-xs px-2 py-1 bg-information-100 text-information-700 rounded">
                              {genderLabel}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-textLight">
                          Number of {leaveType.label} days to allocate annually
                          {isGenderSpecific && ` (Only for ${genderLabel.toLowerCase()} employees)`}
                        </p>
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          placeholder="Days"
                          min="0"
                          value={leaveTypeDays[leaveTypeId] || ""}
                          className="w-full px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                          onChange={(e) => {
                            const days = e.target.value;
                            setLeaveTypeDays(prev => ({
                              ...prev,
                              [leaveTypeId]: days ? parseInt(days) : ""
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                {leaveTypes.length === 0 && (
                  <p className="text-sm text-textLight text-center py-4">
                    No leave types available. Please create leave types first.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Allocation Summary */}
          {Object.values(leaveTypeDays).filter(days => days && parseInt(days) > 0).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-theme mb-3">📋 Allocation Summary</h3>
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <p className="text-sm font-medium text-theme mb-2">The following will be allocated to all employees:</p>
                <div className="space-y-2">
                  {leaveTypes.map((leaveType) => {
                    const leaveTypeId = leaveType.id || leaveType.value;
                    const days = leaveTypeDays[leaveTypeId];
                    if (days && parseInt(days) > 0) {
                      return (
                        <div key={leaveTypeId} className="flex items-center justify-between bg-white p-2 rounded">
                          <span className="text-sm text-theme">
                            {leaveType.label} {leaveType.code && `(${leaveType.code})`}
                          </span>
                          <span className="text-sm font-semibold text-theme">
                            {days} days
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <p className="text-xs text-textLight">
                    <strong>Year:</strong> {annualAllocationData.year || "Not selected"} | 
                    <strong className="ml-2">Period:</strong> {annualAllocationData.allocation_date || "Not set"} to {annualAllocationData.valid_until || "Not set"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Run Annual Allocation Button */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="bg-warning-50 border border-warning-200 rounded p-4 mb-4">
              <p className="text-xs font-medium text-theme mb-2">⚠️ Before Running Annual Allocation:</p>
              <ul className="text-xs text-textLight space-y-1 list-disc list-inside">
                <li>Verify the selected year is correct</li>
                <li>Check allocation rules for each leave type</li>
                <li>Ensure all employees are eligible</li>
                <li>This will allocate leave to all employees across the organization</li>
              </ul>
            </div>
            <DefaultButton
              variant="primary"
              name={`Run Annual Allocation for ${annualAllocationData.year || "Selected Year"}`}
              onClick={handleAnnualAllocationSubmit}
              disabled={
                !annualAllocationData.year || 
                !annualAllocationData.allocation_date || 
                !annualAllocationData.valid_until || 
                leaveTypes.length === 0 ||
                Object.values(leaveTypeDays).filter(days => days && parseInt(days) > 0).length === 0
              }
            />
            <p className="text-xs text-textLight mt-2">
              This will allocate annual leave balances to all eligible employees based on your configuration.
            </p>
            {Object.values(leaveTypeDays).filter(days => days && parseInt(days) > 0).length === 0 && (
              <p className="text-xs text-warning mt-2">
                ⚠️ Please set allocation days for at least one leave type above.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Allocation History */}
      {allocationMode === "history" && (
        <div className="bg-light rounded-lg shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <h3 className="text-lg font-semibold text-theme">Allocation History</h3>
            <p className="text-sm text-textLight mt-1">
              View complete history of all leave allocations made to employees. Track who allocated what, when, and to whom.
            </p>
            <div className="bg-success-50 border border-success-200 rounded p-2 mt-3">
              <p className="text-xs font-medium text-theme mb-1">✅ What's Shown:</p>
              <p className="text-xs text-textLight">
                Complete history of leave allocations including employee name, leave type, days allocated, allocation date, expiry date (if any), and who made the allocation. This helps track all leave balance changes and maintain transparency.
              </p>
            </div>
          </div>
          <div className="p-4 border-b border-borderColor">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-theme">Filter by Type:</label>
                <select
                  value={historyFilterType}
                  onChange={(e) => {
                    setHistoryFilterType(e.target.value);
                    // Reset filters when type changes
                    setHistoryFilters({
                      user_id: "",
                      leave_type_id: "",
                      department_id: "",
                    });
                  }}
                  className="px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                >
                  <option value="all">All Types</option>
                  <option value="individual">Individual</option>
                  <option value="bulk">Bulk</option>
                </select>
              </div>

              {/* Individual Allocation Filters */}
              {(historyFilterType === "all" || historyFilterType === "individual") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-light p-4 rounded">
                  <div>
                    <label className="text-sm font-medium text-theme mb-1 block">Employee (Optional)</label>
                    <select
                      value={historyFilters.user_id}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, user_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                    >
                      <option value="">All Employees</option>
                      {staffList.map((staff) => (
                        <option key={staff.value} value={staff.value}>
                          {staff.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-theme mb-1 block">Leave Type (Optional)</label>
                    <select
                      value={historyFilters.leave_type_id}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, leave_type_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                    >
                      <option value="">All Leave Types</option>
                      {leaveTypes.map((leaveType) => (
                        <option key={leaveType.id || leaveType.value} value={leaveType.id || leaveType.value}>
                          {leaveType.label} {leaveType.code && `(${leaveType.code})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Bulk Allocation Filters */}
              {historyFilterType === "bulk" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-light p-4 rounded">
                  <div>
                    <label className="text-sm font-medium text-theme mb-1 block">Department (Optional)</label>
                    <select
                      value={historyFilters.department_id}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, department_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                      disabled={departmentsLoading}
                    >
                      <option value="">All Departments</option>
                      {(departments || []).map((dept) => (
                        <option key={dept.value} value={dept.value}>
                          {dept.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-theme mb-1 block">Leave Type (Optional)</label>
                    <select
                      value={historyFilters.leave_type_id}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, leave_type_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                    >
                      <option value="">All Leave Types</option>
                      {leaveTypes.map((leaveType) => (
                        <option key={leaveType.id || leaveType.value} value={leaveType.id || leaveType.value}>
                          {leaveType.label} {leaveType.code && `(${leaveType.code})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
          <PageTableHeader
            isSearch
            onSearchChange={handleHistorySearchChange}
            shorting
            sortingModule="leaveAllocation"
            onSortingChange={historySortingUIProps?.onSortingChange}
            sortingProps={historySortingUIProps}
          />
          <div className="p-4">
            <CommonTable
              search={historySearch}
              TableHeaderData={[
                { label: "Allocation Type", key: "allocation_type" },
                { label: "Employee", key: "employee" },
                { label: "Leave Type", key: "leave_type" },
                { label: "Days Allocated", key: "days" },
                { label: "Allocation Date", key: "allocation_date" },
                { label: "Valid Until", key: "valid_until" },
                { label: "Allocated By", key: "allocated_by" },
                { label: "Reason", key: "reason" },
                { label: "Status", key: "status" },
              ]}
              tabledata={historyTableData?.data || historyTableData || []}
              currentPage={historyCurrentPage}
              totalData={historyTotalData}
              setCurrentPage={handleHistoryPageChange}
              loading={historyLoading}
            />
          </div>
        </div>
      )}

      {/* Staff Selection Modal */}
      <StaffModal
        show={showStaffModal}
        closeModal={() => setShowStaffModal(false)}
        onSubmit={handleEmployeeSelect}
        pre_value={selectedEmployee}
      />
    </div>
  );
};

export default LeaveAllocationSection;

